// @flow

import * as React from 'react';
import { Device, Environment } from '@ta/lithium.context';
import EventBoundary from '@ta/events.event-boundary';
import Window from '@ta/events.window';
import AttachedOverlay from '@ta/overlays.attached-overlay';
import FullscreenModal from '@ta/modal.fullscreen';
import { ShadowBox, Target } from '@ta/overlays.pieces';

type Props = {|
  contents: (close?: () => mixed) => React.Node,
  children: (
    (?HTMLElement) => void,
    open: () => mixed,
    close: () => mixed,
    isOpen: boolean,
  ) => React.Node,
  position: 'above' | 'below' | 'leftOf' | 'rightOf',
  alignEdge?: 'top' | 'bottom' | 'left' | 'right',
  fixed?: boolean,
|};

/** Function return position of overlay in dependence with target  */
export const getPosition = (isRtl: boolean, position: any) => {
  if (isRtl) {
    if (position === 'rightOf') {
      return 'leftOf';
    }
    if (position === 'leftOf') {
      return 'rightOf';
    }
  }
  return position;
};

/** Function return alignment edge of overlay in dependence with target */
export const getAlignEdge = (isRtl: boolean, alignEdge: any) => {
  if (isRtl) {
    if (alignEdge === 'right') {
      return 'left';
    }
    if (alignEdge === 'left') {
      return 'right';
    }
  }
  return alignEdge;
};

const ResponsiveAttachedOverlay = (props: Props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const close = React.useCallback(() => setIsOpen(false), []);
  const open = React.useCallback(() => setIsOpen(true), []);
  const {
    locale: { isRtl },
  } = React.useContext(Environment);
  const { viewportCategory } = React.useContext(Device);
  const position = getPosition(isRtl, props.position);
  const alignEdge = getAlignEdge(isRtl, props.alignEdge);

  const getOverlay = target =>
    viewportCategory === 'MOBILE' ? (
      <FullscreenModal enableCloseIcon onClose={close} disableAutoFocus>
        {props.contents(close)}
      </FullscreenModal>
    ) : (
      // $FlowFixMe: Target is nonnull here, because of the check on line 31.
      <AttachedOverlay
        target={target}
        position={position}
        alignEdge={alignEdge}
        fixed={props.fixed}
      >
        <ShadowBox>{props.contents()}</ShadowBox>
      </AttachedOverlay>
    );

  const getEventBoundary = target =>
    target ? <EventBoundary events={['onClick']}>{getOverlay(target)}</EventBoundary> : null;

  return (
    <Target>
      {(setTarget, target) => (
        <>
          {isOpen && (
            <React.Fragment>
              {getEventBoundary(target)}
              <Window event="click" callback={close} />
            </React.Fragment>
          )}
          {props.children(setTarget, open, close, isOpen)}
        </>
      )}
    </Target>
  );
};

export default ResponsiveAttachedOverlay;
